import { useEffect, useRef, useState } from 'react';

function RhythmCorner({ corner }: { corner: 'tl' | 'tr' | 'bl' | 'br' }) {
  return (
    <div
      className="rhythm-corner absolute w-[100px] h-[100px] pointer-events-none"
      data-corner={corner}
      style={
        corner === 'tl'
          ? { top: '1.5rem', left: '1.5rem' }
          : corner === 'tr'
            ? { top: '1.5rem', right: '1.5rem' }
            : corner === 'bl'
              ? { bottom: '1.5rem', left: '1.5rem' }
              : { bottom: '1.5rem', right: '1.5rem' }
      }
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="rhythm-line" />
      ))}
    </div>
  );
}

export default function SymbolStory() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(section);

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.top < windowHeight && rect.bottom > 0) {
        const progress = Math.min(
          1,
          Math.max(0, (windowHeight - rect.top) / (windowHeight + rect.height))
        );
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const entranceProgress = Math.min(1, scrollProgress * 3);
  const exitProgress = Math.max(0, (scrollProgress - 0.6) * 2.5);
  const patternOpacity = entranceProgress * (1 - exitProgress);
  const contentOpacity = entranceProgress * (1 - exitProgress);
  const contentY = (1 - entranceProgress) * 12 - exitProgress * 10;

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[50vh] py-16 bg-[var(--vyera-black)] flex items-center justify-center overflow-hidden"
      style={{ zIndex: 50 }}
    >
      {/* Narrower content block: max-width + corners and text inside */}
      <div
        className="relative w-full max-w-3xl mx-auto min-h-[50vh] flex items-center justify-center px-8"
        style={{ opacity: patternOpacity * 0.9 }}
      >
        {/* Rhythm animation in the four corners of this block */}
        <div className="absolute inset-0 pointer-events-none">
          <RhythmCorner corner="tl" />
          <RhythmCorner corner="tr" />
          <RhythmCorner corner="bl" />
          <RhythmCorner corner="br" />
        </div>

        {/* Content — Soft Cream text */}
        <div
          className="relative z-10 text-center"
          style={{
            opacity: contentOpacity,
            transform: `translateY(${contentY}vh)`
          }}
        >
          <h2
            className={`vyera-heading-lg text-[var(--vyera-cream)] mb-6 transition-all duration-700 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            A mark of rhythm.
          </h2>

          <p
            className={`vyera-body text-[var(--vyera-warm-grey)] max-w-md mx-auto transition-all duration-700 delay-100 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            The V-Frequency represents movement in its purest form.
          </p>
        </div>
      </div>
    </section>
  );
}
